/**
 * Shared helpers for persisting the campaign / referral-source code
 * across the Career Lobby → Job Page → Application Form journey.
 *
 * The value is kept in `sessionStorage` so it survives client-side
 * navigations within the same tab.  To also survive "open in new tab"
 * clicks, `JobCard` appends the stored value back onto the job-page URL.
 */

/** sessionStorage key used by both the lobby page and the application form. */
export const REFERRAL_SOURCE_STORAGE_KEY = "referralSourceCode"

/** Query-string keys we look for (in priority order). */
export const REFERRAL_QUERY_KEYS = ["source", "code", "ref", "referid"] as const

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Read `source` / `code` / `ref` from the given search-params object and,
 * if a value is found, persist it in `sessionStorage`.
 *
 * Works on both the Next.js `searchParams` prop shape
 * (`Record<string, string | string[] | undefined>`) and a plain
 * `URLSearchParams` instance.
 */
export function storeReferralSource(
  searchParams?: Record<string, string | string[] | undefined> | URLSearchParams,
): void {
  if (typeof window === "undefined" || !searchParams) return

  let nextValue: string | undefined

  if (searchParams instanceof URLSearchParams) {
    nextValue = REFERRAL_QUERY_KEYS.map((key) => searchParams.get(key))
      .find((v) => v != null && v.trim() !== "")
      ?.trim()
  } else {
    nextValue = REFERRAL_QUERY_KEYS.map((key) => {
      const raw = searchParams[key]
      const candidate = Array.isArray(raw) ? raw.find(Boolean) : raw
      return candidate?.toString().trim()
    }).find(Boolean)
  }

  if (nextValue) {
    sessionStorage.setItem(REFERRAL_SOURCE_STORAGE_KEY, nextValue)
  }
}

/**
 * Return the stored referral-source value (or `""`).
 * Safe to call during SSR – returns `""` when `window` is unavailable.
 */
export function getReferralSource(): string {
  if (typeof window === "undefined") return ""
  return sessionStorage.getItem(REFERRAL_SOURCE_STORAGE_KEY) || ""
}

/**
 * Build a query-string fragment that can be appended to a job-page URL
 * so the referral code survives "open in new tab" clicks.
 *
 * Returns e.g. `"?source=123"` or `""` when nothing is stored.
 */
export function buildReferralQueryString(): string {
  const value = getReferralSource()
  if (!value) return ""
  return `?source=${encodeURIComponent(value)}`
}
